import { useEffect, useRef } from 'react'

// A single, reusable AdSense display unit that is safe for our SPA + prerender
// setup:
//   - Renders nothing unless we're in production AND both the AdSense client and
//     an ad slot are configured, so no empty ad boxes appear in dev, in the
//     prerendered snapshot, or before you've created the unit in AdSense.
//   - Pushes to `adsbygoogle` once per mount. React Router remounts pages on
//     navigation, so each visit re-requests the ad; a ref guards against the
//     double-invoke that would otherwise trigger AdSense's "already have ads"
//     error (React StrictMode in dev, fast re-renders in prod).
//
// All placements share one responsive unit via VITE_ADSENSE_SLOT, so you only
// need to create a single "Display" ad unit in AdSense. Pass an explicit `slot`
// to override per-placement (e.g. for separate reporting).

// Publisher + ad-unit IDs are public (they ship in the page HTML), so we keep
// them as literal fallbacks and let env vars override for other environments.
// `panellhniesplus-inarticle` responsive Display unit:
const CLIENT = import.meta.env.VITE_ADSENSE_CLIENT || 'ca-pub-8030771741382130'
const IS_PROD = import.meta.env.VITE_ENVIRONMENT === 'prod'
const DEFAULT_SLOT = import.meta.env.VITE_ADSENSE_SLOT || '7150559796'

function AdUnit({
  slot = DEFAULT_SLOT,
  format = 'auto',
  responsive = true,
  className = '',
  label = true,
}) {
  const insRef = useRef(null)
  const pushed = useRef(false)

  const enabled = IS_PROD && !!CLIENT && !!slot

  useEffect(() => {
    if (!enabled || pushed.current) return
    const el = insRef.current
    // Skip if AdSense already filled this element.
    if (!el || el.getAttribute('data-adsbygoogle-status')) return
    try {
      ;(window.adsbygoogle = window.adsbygoogle || []).push({})
      pushed.current = true
    } catch {
      // adsbygoogle.js not ready yet — the loader will process the <ins> once it
      // arrives, so this is safe to ignore.
    }
  }, [enabled, slot])

  if (!enabled) return null

  return (
    <div className={`my-8 ${className}`} aria-hidden="true">
      {label ? (
        <div className="mb-1 text-center text-[10px] uppercase tracking-wider text-gray-400">
          Διαφήμιση
        </div>
      ) : null}
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  )
}

export default AdUnit
